import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import Masonry from '@mui/lab/Masonry';
import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useScope, useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import CustomerTitleItems from '@sophocles/components/App/CustomerTitleItems';
import TitleBar from '@sophocles/components/App/TitleBar';
import { fetchCurfewReportEnabled } from '@sophocles/entities/Customer';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';

import CustomerSiteListing from './components/CustomerSiteListing';
import ReportLinkItem from './components/ReportLinkItem';
import ReportSection from './components/ReportSection';

const LAMAR_CUSTOMER_ID = 2;

function CustomerReportsView(props) {
  const {
    match: { params },
    t,
  } = props;

  const { customer } = useScope();
  const { user, permissions } = useUser();
  const dispatch = useDispatch();
  const [curfewReportEnabled, setCurfewReportEnabled] = useState(false);
  const separateTenant = useSelector(selectSeparateTenant);

  const baseLink = `/c/${params.cust_id}`;
  const customerId = Number(params.cust_id);
  const canSeeCurfewReport = curfewReportEnabled && user.get('is_superuser', false);
  const canViewInventory = permissions.canSeeInventory;
  const isLamar = customerId === LAMAR_CUSTOMER_ID;
  const isBillboard = customer?.is_billboard ?? true;

  useEffect(() => {
    const cid = customer?.id ?? null;
    if (cid != null) {
      dispatch(fetchCurfewReportEnabled(cid)).then(setCurfewReportEnabled);
    }
  }, [customer, dispatch]);

  return (
    <AppNav page="customer_reports" notFullHeight={true} {...props}>
      <Box sx={{ top: '64px', mx: 2, pb: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <TitleBar
          showBackButton
          title={t('reports.reports')}
          items={<CustomerTitleItems user={user} customer={customer} params={params} page="reports" />}
        />

        <Masonry columns={{ xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} spacing={2} sx={{ m: 0 }}>
          <ReportSection icon={<AssignmentOutlinedIcon />} title={t('reports.standardReports')}>
            <ReportLinkItem
              to={`${baseLink}/reports/alarmlivesummary`}
              primary={t('reports.alarmAndMaintenanceLiveSummary')}
            />
            {canSeeCurfewReport && (
              <ReportLinkItem to={`${baseLink}/reports/curfew`} primary={t('reports.curfewReport')} />
            )}
            <ReportLinkItem
              to={`${baseLink}/reports/maintenance?alarms=offline`}
              primary={t('reports.offlineReport')}
            />
            <ReportLinkItem to={`${baseLink}/reports/maintenance`} primary={t('reports.alarmMaintenance')} />
            <CustomerSiteListing isBillboard={isBillboard} customerId={customer?.id} customerName={customer?.name} />
          </ReportSection>

          {canViewInventory && (
            <ReportSection icon={<Inventory2OutlinedIcon />} title={t('reports.inventoryReports')}>
              <ReportLinkItem to={`${baseLink}/inventory`} primary={t('reports.inventoryList')} />
            </ReportSection>
          )}

          {isLamar && !separateTenant && (
            <ReportSection icon={<BusinessOutlinedIcon />} title={t('reports.lamarReports')}>
              <ReportLinkItem to={`${baseLink}/gulfpower12month`} primary={t('reports.gulfPower12Month')} />
            </ReportSection>
          )}
        </Masonry>
      </Box>
    </AppNav>
  );
}

export default withTranslation()(CustomerReportsView);
